import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./efficientFrontierChart.module.scss";
import { formatLocalizedDate } from "utils/dateUtils";
import i18n from "i18n/i18n";

type DataPoint = { risk: number; return: number };

type Props = {
  data: DataPoint[];
  selectedPlan: DataPoint;
  portfolio: DataPoint;
};

const EfficientFrontierChart: React.FC<Props> = ({
  data,
  selectedPlan,
  portfolio,
}) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const sortedData = [...data].sort((a, b) => a.risk - b.risk);

  const chartConfig = useMemo(() => {
    const allPoints = [...data, selectedPlan, portfolio];

    const maxRisk = Math.ceil(Math.max(...allPoints.map((p) => p.risk)));
    const riskRange = maxRisk;
    const riskStep = Math.ceil(riskRange / 6);
    const riskTicks = Array.from(
      { length: Math.floor(riskRange / riskStep) + 1 },
      (_, i) => i * riskStep,
    );

    const maxReturn = Math.ceil(Math.max(...allPoints.map((p) => p.return)));
    const returnRange = maxReturn;
    const returnStep = Math.ceil(returnRange / 6);
    const returnTicks = Array.from(
      { length: Math.floor(returnRange / returnStep) + 1 },
      (_, i) => i * returnStep,
    );

    return {
      riskDomain: [0, maxRisk],
      returnDomain: [0, maxReturn],
      riskTicks,
      returnTicks,
    };
  }, [data, selectedPlan, portfolio]);

  const axisStyle = {
    fontSize: "12px",
    fontFamily: "inherit",
    fontWeight: 400,
    fill: colorsConfig.FinecoBlack,
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "18px",
    fill: colorsConfig.FinecoBlack,
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          className={styles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {payload.map((entry: any, index: number) => {
            if (entry.value === undefined) return null;
            return (
              <div key={`value-${index}`}>
                <p
                  style={{
                    color: entry.color,
                    margin: "3px 0",
                    fontSize: "12px",
                  }}
                >
                  VaR: {payload[0].payload.risk.toFixed(2)}%
                </p>
                <p
                  style={{
                    color: entry.color,
                    margin: "3px 0",
                    fontSize: "12px",
                  }}
                >
                  Rendimento: {payload[0].payload.return.toFixed(2)}%
                </p>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={350} debounce={500}>
      <ComposedChart margin={{ top: 20, right: 20, bottom: 30, left: 15 }}>
        <Legend
          verticalAlign="top"
          height={36}
          wrapperStyle={{
            marginTop: "-10px",
            fontSize: "12px",
            fontFamily: "inherit",
          }}
          iconSize={10}
          iconType="circle"
          formatter={(value) => (
            <span style={{ marginRight: "20px" }}>{value}</span>
          )}
        />
        <CartesianGrid stroke={colorsConfig.FinecoGrayLight1} />
        <XAxis
          type="number"
          dataKey="risk"
          name="VAR ATTESO"
          domain={chartConfig.riskDomain}
          ticks={chartConfig.riskTicks}
          tickFormatter={(value) => `${value}%`}
          label={{
            value: t("portfolio.efficientFrontierBottomLabel"),
            position: "insideBottom",
            dy: 25,
            ...labelStyle,
          }}
          tick={axisStyle}
          axisLine={{ stroke: colorsConfig.Gray1 }}
          tickLine={false}
        />
        <YAxis
          type="number"
          dataKey="return"
          name="% Rendimento medio annuo atteso"
          domain={chartConfig.returnDomain}
          ticks={chartConfig.returnTicks}
          tickFormatter={(value) => `${value}%`}
          label={{
            value: t("portfolio.efficientFrontierLeftLabel"),
            angle: -90,
            position: "center",
            dx: -40,
            ...labelStyle,
          }}
          tick={(props) => {
            const { x, y, payload } = props;
            // Hide the zero on X-axis
            if (payload.value === 0) return null;
            return (
              <g transform={`translate(${x},${y})`}>
                <text
                  x={-10}
                  y={0}
                  dy={16}
                  textAnchor="middle"
                  fill={axisStyle.fill}
                  fontSize={axisStyle.fontSize}
                  fontFamily={axisStyle.fontFamily}
                  fontWeight={axisStyle.fontWeight}
                >
                  {`${payload.value}%`}
                </text>
              </g>
            );
          }}
          axisLine={{ stroke: colorsConfig.Gray1 }}
          tickLine={false}
        />
        {/* <Tooltip
          cursor={false}
          content={({ active, payload }) => {
            if (active && payload?.length) {
              return (
                <div className={styles.tooltipContainer}>
                  <div className={styles.tooltipRow}>
                    VaR: {payload[0].payload.risk.toFixed(2)}%
                  </div>
                  <div className={styles.tooltipRow}>
                    Rendimento: {payload[0].payload.return.toFixed(2)}%
                  </div>
                </div>
              );
            }
            return null;
          }}
        /> */}
        <Tooltip content={<CustomTooltip />} />

        <Line
          type="monotone"
          data={sortedData}
          dataKey="return"
          name={t("portfolio.efficientFrontierLine1Label")}
          stroke={colorsConfig.FinecoBlack}
          strokeWidth={2}
          dot={{ fill: "white", stroke: colorsConfig.FinecoBlack, r: 6 }}
          connectNulls
          isAnimationActive={false}
        />
        <Scatter
          name={t("portfolio.efficientFrontierLine2Label")}
          data={[selectedPlan]}
          fill={colorsConfig.orange}
          r={6}
          isAnimationActive={false}
        />
        <Scatter
          name={t("portfolio.efficientFrontierLine3Label")}
          data={[portfolio]}
          fill={colorsConfig.Green}
          r={6}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default EfficientFrontierChart;
